import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { onColorLinksCss } from '../utils/onColorLinks';

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

interface Amenity {
  title?: string | null;
  group?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Slide definitions
// ============================================================================

// Each slide is its own slot group so the generator can pick a varied set of
// photos (any collection) instead of the first N gallery items. Fields have no
// defaultField: slot-group defaults can't filter photo_gallery by title, so an
// unset slide falls back to the matching index of the filtered gallery binding.
// Literals (not a factory) keep the schema statically extractable.
const communitySlide1Def: SlotGroupDefinition = {
  label: 'Community Slide 1',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick the single most desirable, eye-catching photo of a shared community amenity at this property — the space a prospective resident would most want to see. Not a plain building exterior, unit interior, floor plan, site map, parking, or empty hallway.',
    context:
      'Community Slide 1 of 6 in the Community Amenities photo slider on the Amenities page — the first image visitors see. 4:3 landscape slide. Slides are ordered by appeal: strongest amenities first. Each of the 6 slides must show a different amenity and a different photo.',
  },
};

const communitySlide2Def: SlotGroupDefinition = {
  label: 'Community Slide 2',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick the next most desirable shared community amenity — a different space from slide 1, not another angle of the same room. Not a plain building exterior, unit interior, floor plan, site map, or parking.',
    context:
      'Community Slide 2 of 6 in the Community Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different amenity and photo from community slides 1 and 3–6.',
  },
};

const communitySlide3Def: SlotGroupDefinition = {
  label: 'Community Slide 3',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick another highly desirable shared community amenity not used on slides 1–2. Prefer variety: if earlier picks were indoor spaces, favor an outdoor amenity area, and vice versa. Not a plain building exterior, unit interior, floor plan, site map, or parking.',
    context:
      'Community Slide 3 of 6 in the Community Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different amenity and photo from community slides 1, 2, and 4–6.',
  },
};

const communitySlide4Def: SlotGroupDefinition = {
  label: 'Community Slide 4',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick the next-best shared amenity or common area not shown on slides 1–3. Not a plain building exterior, unit interior, floor plan, site map, or parking. If no unused amenity photo exists, leave empty rather than repeat one.',
    context:
      'Community Slide 4 of 6 in the Community Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different amenity and photo from community slides 1–3, 5, and 6.',
  },
};

const communitySlide5Def: SlotGroupDefinition = {
  label: 'Community Slide 5',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick another unused shared amenity or common-area photo. Not a plain building exterior, unit interior, floor plan, site map, or parking. Leave empty rather than repeat a photo.',
    context:
      'Community Slide 5 of 6 in the Community Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different amenity and photo from community slides 1–4 and 6.',
  },
};

const communitySlide6Def: SlotGroupDefinition = {
  label: 'Community Slide 6',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one more unused amenity or common-area photo to round out the set. A single strong exterior, entry, or grounds shot is acceptable here only if no unused amenity photo remains. Leave empty rather than repeat a photo.',
    context:
      'Community Slide 6 of 6 (last) in the Community Amenities photo slider on the Amenities page. 4:3 landscape slide. Different photo from community slides 1–5.',
  },
};

const apartmentSlide1Def: SlotGroupDefinition = {
  label: 'Apartment Slide 1',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick the single most desirable apartment interior photo — a bright, furnished or finished main living space that best sells the home. Not an exterior, a shared amenity, a floor plan, or a closet/laundry/utility nook.',
    context:
      'Apartment Slide 1 of 6 in the Interior Amenities photo slider on the Amenities page — the first image visitors see. 4:3 landscape slide. Slides are ordered by appeal: strongest rooms first. Each of the 6 slides must show a different room or feature and a different photo.',
  },
};

const apartmentSlide2Def: SlotGroupDefinition = {
  label: 'Apartment Slide 2',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick the next most desirable apartment interior photo, showing a different room type than slide 1. Not an exterior, a shared amenity, a floor plan, or a closet/laundry/utility nook.',
    context:
      'Apartment Slide 2 of 6 in the Interior Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different room and photo from apartment slides 1 and 3–6.',
  },
};

const apartmentSlide3Def: SlotGroupDefinition = {
  label: 'Apartment Slide 3',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick another appealing apartment interior photo of a room type not shown on slides 1–2. Not an exterior, a shared amenity, a floor plan, or a closet/laundry/utility nook.',
    context:
      'Apartment Slide 3 of 6 in the Interior Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different room and photo from apartment slides 1, 2, and 4–6.',
  },
};

const apartmentSlide4Def: SlotGroupDefinition = {
  label: 'Apartment Slide 4',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick an unused apartment photo that highlights an in-unit feature or finish (e.g. a private outdoor space, view, or appliance/finish detail — only whatever is actually pictured). Leave empty rather than repeat a photo.',
    context:
      'Apartment Slide 4 of 6 in the Interior Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different photo from apartment slides 1–3, 5, and 6.',
  },
};

const apartmentSlide5Def: SlotGroupDefinition = {
  label: 'Apartment Slide 5',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick another unused apartment interior photo — a different room or in-unit feature than earlier slides. Not an exterior, a shared amenity, or a floor plan. Leave empty rather than repeat a photo.',
    context:
      'Apartment Slide 5 of 6 in the Interior Amenities photo slider on the Amenities page. 4:3 landscape slide. Slides are ordered by appeal. Different photo from apartment slides 1–4 and 6.',
  },
};

const apartmentSlide6Def: SlotGroupDefinition = {
  label: 'Apartment Slide 6',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
    title: { label: 'Caption', type: 'text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text', title: 'title' },
  },
  generator: {
    prompt:
      'Pick one more unused apartment interior photo to round out the set. Not an exterior, a shared amenity, or a floor plan. Leave empty rather than repeat a photo.',
    context:
      'Apartment Slide 6 of 6 (last) in the Interior Amenities photo slider on the Amenities page. 4:3 landscape slide. Different photo from apartment slides 1–5.',
  },
};

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface Slide {
  key: string;
  photo: GalleryPhoto | null;
  /** Wraps the slide body in its EditableSlotGroup (written with a literal groupPath for the schema linter). */
  wrap: (className: string, children: ReactNode) => ReactNode;
}

function SlideBody({ photo, heading }: { photo: GalleryPhoto | null; heading: string }) {
  if (!photo?.image) return <div className="amenities-slide-placeholder">No image</div>;
  return (
    <VibesitesImage
      url={photo.image}
      alt={photo.alt_text || photo.title || heading}
      className="amenities-slider-img"
      sizes="(min-width: 640px) 50vw, 100vw"
      priority
    />
  );
}

interface PhotoSliderProps {
  slides: Slide[];
  heading: string;
}

function PhotoSlider({ slides, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = slides.length;
  const active = count > 0 ? current % count : 0;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map(({ key, photo, wrap }, i) => (
        <Fragment key={key}>
          {wrap(
            `amenities-slide${i === active ? ' amenities-slide--active' : ''}`,
            <SlideBody photo={photo} heading={heading} />,
          )}
        </Fragment>
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Gallery cell
// ============================================================================

interface GalleryCellProps {
  slides: Slide[];
  heading: string;
  cellClassName: string;
}

function GalleryCell({ slides, heading, cellClassName }: GalleryCellProps) {
  const ref = useScrollReveal<HTMLDivElement>();
  const isEditMode = useEditMode()?.isEditMode ?? false;

  // Live: drop empty and duplicate slides. Edit mode: keep every slide so an
  // empty one can still be clicked and filled.
  const seen = new Set<string>();
  const visibleSlides = isEditMode
    ? slides
    : slides.filter(({ photo }) => {
        const url = photo?.image;
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      });

  if (visibleSlides.length === 0) return null;

  return (
    <div ref={ref} className={`reveal-fade-up amenities-cell amenities-cell--gallery ${cellClassName}`}>
      <PhotoSlider slides={visibleSlides} heading={heading} />
    </div>
  );
}

/** Slot-group values win once set (generated or edited); otherwise the filtered gallery photo at the same index. */
function slidePhoto(
  values: Record<string, string | null>,
  fallback: GalleryPhoto | undefined,
): GalleryPhoto | null {
  if (values.image) return { image: values.image, alt_text: values.alt, title: values.title };
  return fallback ?? null;
}

// ============================================================================
// Amenity list cell (paginated text column)
// ============================================================================

const ITEMS_PER_PAGE = 8;

const MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'the', 'to', 'with',
]);

// Canonical casing for acronyms/abbreviations, keyed by lowercase form. Whole-word
// entries (containing - or /) are matched before splitting on those separators.
const CANONICAL_WORDS = new Map(
  [
    'Wi-Fi', 'W/D', 'A/C', '24/7',
    'TV', 'TVs', 'HDTV', 'HD', '4K', 'RV', 'RVs', 'EV', 'EVs', 'BBQ', 'BBQs', 'HVAC',
    'USB', 'LED', 'DVD', 'ATM', 'ADA', 'HOA', 'UV', 'AC', 'CCTV', 'XL',
  ].map(word => [word.toLowerCase(), word]),
);
CANONICAL_WORDS.set('wifi', 'Wi-Fi');

function hasAsterisk(value: string | null | undefined): boolean {
  return !!value && value.includes('*');
}

function titleCasePart(part: string): string {
  const canonical = CANONICAL_WORDS.get(part.toLowerCase());
  if (canonical) return canonical;
  return part.replace(/^(\P{L}*)(\p{L})/u, (_, lead: string, ch: string) => lead + ch.toUpperCase());
}

function titleCaseWord(word: string): string {
  const match = word.match(/^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}]*)$/u);
  if (!match) return word;
  const [, lead, core, trail] = match;
  const whole = CANONICAL_WORDS.get(core.toLowerCase());
  if (whole) return lead + whole + trail;
  return lead + core.split(/([-/])/).map(titleCasePart).join('') + trail;
}

// Mixed-case names keep their inner letters so unlisted acronyms stay intact;
// names entered entirely in capitals are lowercased first so every entry matches.
// Known acronyms (TV, RV, EV, Wi-Fi, ...) always get their canonical casing.
function formatAmenityName(value: string): string {
  const starred = hasAsterisk(value);
  let base = value.replace(/\*/g, '').trim().replace(/\s+/g, ' ');
  if (!base) return '';
  if (!/\p{Ll}/u.test(base)) base = base.toLowerCase();

  const words = base.split(' ');
  const titled = words
    .map((word, i) => {
      const isEdge = i === 0 || i === words.length - 1;
      if (!isEdge && MINOR_WORDS.has(word.toLowerCase())) return word.toLowerCase();
      return titleCaseWord(word);
    })
    .join(' ');

  return starred ? `${titled}*` : titled;
}

interface AmenityListCellProps {
  headingNode: ReactNode;
  items: Amenity[];
  cellClassName: string;
  disclaimer?: ReactNode;
}

function AmenityListCell({ headingNode, items, cellClassName, disclaimer }: AmenityListCellProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  // Clamp in case the collection shrinks (e.g. live editing) below the current page.
  const currentPage = Math.min(page, pageCount - 1);
  const isPaginated = pageCount > 1;

  const visibleItems = useMemo(() => {
    if (!isPaginated) return items;
    const start = currentPage * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  }, [items, isPaginated, currentPage]);

  if (items.length === 0) return null;

  return (
    <div ref={colRef} className={`reveal-fade-up amenities-cell amenities-cell--list ${cellClassName}`}>
      {headingNode}
      <ul className="amenities-list-items">
        {visibleItems.map((item, i) => (
          <li key={currentPage * ITEMS_PER_PAGE + i} className="amenities-list-item">
            <span className="amenities-list-bullet" aria-hidden="true" />
            <span>{formatAmenityName(item.title || '')}</span>
          </li>
        ))}
      </ul>
      {disclaimer}
      {isPaginated && (
        <nav className="amenities-list-pagination" aria-label="Amenities pagination">
          <button
            type="button"
            className="amenities-list-page-btn"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 0}
            aria-label="Previous amenities"
          >
            <span aria-hidden="true">&#8592;</span>
          </button>
          <button
            type="button"
            className="amenities-list-page-btn"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === pageCount - 1}
            aria-label="Next amenities"
          >
            <span aria-hidden="true">&#8594;</span>
          </button>
        </nav>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const interiorItems = useCollectionBinding<Amenity>('amenitieslist.interiorItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communitySlide1 = useSlotGroup('amenitieslist.community_slide_1', communitySlide1Def);
  const communitySlide2 = useSlotGroup('amenitieslist.community_slide_2', communitySlide2Def);
  const communitySlide3 = useSlotGroup('amenitieslist.community_slide_3', communitySlide3Def);
  const communitySlide4 = useSlotGroup('amenitieslist.community_slide_4', communitySlide4Def);
  const communitySlide5 = useSlotGroup('amenitieslist.community_slide_5', communitySlide5Def);
  const communitySlide6 = useSlotGroup('amenitieslist.community_slide_6', communitySlide6Def);

  const apartmentSlide1 = useSlotGroup('amenitieslist.apartment_slide_1', apartmentSlide1Def);
  const apartmentSlide2 = useSlotGroup('amenitieslist.apartment_slide_2', apartmentSlide2Def);
  const apartmentSlide3 = useSlotGroup('amenitieslist.apartment_slide_3', apartmentSlide3Def);
  const apartmentSlide4 = useSlotGroup('amenitieslist.apartment_slide_4', apartmentSlide4Def);
  const apartmentSlide5 = useSlotGroup('amenitieslist.apartment_slide_5', apartmentSlide5Def);
  const apartmentSlide6 = useSlotGroup('amenitieslist.apartment_slide_6', apartmentSlide6Def);

  useRevealStyles();

  const communitySlides: Slide[] = [
    {
      key: 'community_slide_1',
      photo: slidePhoto(communitySlide1, communityPhotos[0]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.community_slide_1" definition={communitySlide1Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'community_slide_2',
      photo: slidePhoto(communitySlide2, communityPhotos[1]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.community_slide_2" definition={communitySlide2Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'community_slide_3',
      photo: slidePhoto(communitySlide3, communityPhotos[2]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.community_slide_3" definition={communitySlide3Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'community_slide_4',
      photo: slidePhoto(communitySlide4, communityPhotos[3]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.community_slide_4" definition={communitySlide4Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'community_slide_5',
      photo: slidePhoto(communitySlide5, communityPhotos[4]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.community_slide_5" definition={communitySlide5Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'community_slide_6',
      photo: slidePhoto(communitySlide6, communityPhotos[5]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.community_slide_6" definition={communitySlide6Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
  ];

  const apartmentSlides: Slide[] = [
    {
      key: 'apartment_slide_1',
      photo: slidePhoto(apartmentSlide1, apartmentPhotos[0]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.apartment_slide_1" definition={apartmentSlide1Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'apartment_slide_2',
      photo: slidePhoto(apartmentSlide2, apartmentPhotos[1]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.apartment_slide_2" definition={apartmentSlide2Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'apartment_slide_3',
      photo: slidePhoto(apartmentSlide3, apartmentPhotos[2]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.apartment_slide_3" definition={apartmentSlide3Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'apartment_slide_4',
      photo: slidePhoto(apartmentSlide4, apartmentPhotos[3]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.apartment_slide_4" definition={apartmentSlide4Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'apartment_slide_5',
      photo: slidePhoto(apartmentSlide5, apartmentPhotos[4]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.apartment_slide_5" definition={apartmentSlide5Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
    {
      key: 'apartment_slide_6',
      photo: slidePhoto(apartmentSlide6, apartmentPhotos[5]),
      wrap: (className, children) => (
        <EditableSlotGroup groupPath="amenitieslist.apartment_slide_6" definition={apartmentSlide6Def} className={className}>
          {children}
        </EditableSlotGroup>
      ),
    },
  ];

  const interiorHasStarred = interiorItems.some(item => hasAsterisk(item.title));

  const hasContent =
    communitySlides.some(slide => slide.photo?.image) ||
    apartmentSlides.some(slide => slide.photo?.image) ||
    communityItems.length > 0 ||
    interiorItems.length > 0;

  return (
    <section className="amenities-section">
      <div className="amenities-container">
        <RichTextContent
          path="amenitieslist.heading"
          label="Heading"
          defaultValue="<p>Designed for Living</p>"
          as="h2"
          className="amenities-heading"
        />
        <RichTextContent
          path="amenitieslist.intro"
          label="Intro"
          defaultValue="<p>Welcome to a world of unmatched luxury and convenience at The Aster. Our amenity offerings redefine the standard of modern living, providing an exquisite backdrop to complement your lifestyle.</p>"
          as="div"
          className="amenities-intro"
        />

        {hasContent && (
          // Two-column source order (cg, ag, cl, al) auto-places into two rows:
          // galleries on top, lists below. Phone stacks to one column and
          // reorders with `order` so each gallery sits above its matching list.
          <div className="amenities-grid">
            <GalleryCell
              slides={communitySlides}
              heading="Community Amenities"
              cellClassName="amenities-cell--cg"
            />
            <GalleryCell
              slides={apartmentSlides}
              heading="Interior Luxuries"
              cellClassName="amenities-cell--ag"
            />
            <AmenityListCell
              cellClassName="amenities-cell--cl"
              headingNode={
                <RichTextContent
                  path="amenitieslist.communityHeading"
                  label="Community Heading"
                  defaultValue="<p>Community Amenities</p>"
                  as="h3"
                  className="amenities-list-heading"
                />
              }
              items={communityItems}
            />
            <AmenityListCell
              cellClassName="amenities-cell--al"
              headingNode={
                <RichTextContent
                  path="amenitieslist.interiorHeading"
                  label="Interior Heading"
                  defaultValue="<p>Interior Amenities</p>"
                  as="h3"
                  className="amenities-list-heading"
                />
              }
              items={interiorItems}
              disclaimer={
                interiorHasStarred ? (
                  <RichTextContent
                    path="amenitieslist.selectUnitsDisclaimer"
                    label="Select Units Disclaimer"
                    defaultValue="<p>*In Select Units</p>"
                    as="div"
                    className="amenities-select-units"
                  />
                ) : null
              }
            />
          </div>
        )}
      </div>

      <style>{`
        ${onColorLinksCss('.amenities-section [data-vibesite-richtext]')}
        .amenities-section {
          position: relative;
          background: var(--vibe-color-primary);
          padding: 8rem 1.5rem 4.5rem;
        }
        /* Slanted top edge: white triangle overlays the top-left,
           leaving a diagonal that rises toward the right */
        .amenities-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 130px;
          background: #ffffff;
          clip-path: polygon(0 0, 100% 0, 0 100%);
        }

        .amenities-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .amenities-heading,
        .amenities-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .amenities-heading {
          font-size: 2.5rem;
          color: #ffffff;
          text-align: center;
          margin: 0 0 1.5rem;
          letter-spacing: 0.05em;
        }

        .amenities-intro,
        .amenities-intro * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-style: normal;
        }
        /* Font size matches HeroSolidBG's blurb (Tailwind text-base md:text-lg):
           1rem below the md breakpoint (768px), 1.125rem at/above it. */
        .amenities-intro {
          font-size: 1rem;
          /* line-height + letter-spacing match HeroSolidBG's blurb
             (Tailwind leading-relaxed = 1.625, no tracking). */
          line-height: 1.625;
          color: #ffffff;
          text-align: center;
          max-width: 900px;
          margin: 0 auto 3rem;
          letter-spacing: normal;
        }
        @media (min-width: 768px) {
          .amenities-intro {
            font-size: 1.125rem;
          }
        }
        .amenities-intro p {
          margin: 0;
        }

        /* Two galleries (row 1) and two lists (row 2), side by side. */
        .amenities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 2.5rem;
          row-gap: 3rem;
          align-items: start;
        }
        .amenities-cell {
          min-width: 0;
        }
        .amenities-cell--gallery {
          position: relative;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
        }
        .amenities-cell--list {
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }

        .amenities-list-heading,
        .amenities-list-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .amenities-list-heading {
          font-size: clamp(1.75rem, 3vw, 2.5rem);
          color: #ffffff;
          margin: 0 0 1.5rem;
          letter-spacing: 0.01em;
          text-align: center;
        }
        .amenities-list-items {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .amenities-list-item {
          display: flex;
          align-items: baseline;
          gap: 0.85rem;
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          line-height: 1.45;
          color: #ffffff;
        }
        .amenities-list-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
          transform: translateY(-0.35em);
        }
        .amenities-list-pagination {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.5rem;
          margin-top: 1.5rem;
          margin-left: -0.5rem;
        }
        .amenities-list-page-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          line-height: 1;
          color: #ffffff;
          background: transparent;
          border: none;
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .amenities-list-page-btn:hover:not(:disabled) {
          transform: scale(1.15);
        }
        .amenities-list-page-btn:disabled {
          opacity: 0.35;
          cursor: default;
        }

        .amenities-select-units,
        .amenities-select-units * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .amenities-select-units {
          margin-top: 1rem;
          font-size: 0.85rem;
          line-height: 1.45;
          color: rgba(255, 255, 255, 0.7);
        }
        .amenities-select-units p {
          margin: 0;
        }

        /* Smaller desktop: keep two columns, compress vertical space. */
        @media (max-width: 1279px) {
          .amenities-section {
            padding: 5.5rem 1.5rem 3.5rem;
          }
          .amenities-section::before {
            height: 90px;
          }
          .amenities-heading {
            font-size: 2.1rem;
            margin-bottom: 1rem;
          }
          .amenities-intro {
            margin-bottom: 2rem;
          }
          .amenities-grid {
            column-gap: 1.75rem;
            row-gap: 1.75rem;
          }
          .amenities-cell--gallery {
            aspect-ratio: 3 / 2;
            box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
          }
          .amenities-list-heading {
            font-size: clamp(1.35rem, 2.4vw, 1.85rem);
            margin-bottom: 0.9rem;
          }
          .amenities-list-items {
            gap: 0.45rem;
          }
          .amenities-list-item {
            font-size: 0.98rem;
            gap: 0.7rem;
          }
          .amenities-list-pagination {
            margin-top: 1rem;
          }
        }

        /* Tablet: still two columns, tighter rhythm than small desktop. */
        @media (max-width: 992px) {
          .amenities-section {
            padding: 5rem 1.25rem 3rem;
          }
          .amenities-section::before {
            height: 70px;
          }
          .amenities-heading {
            font-size: 1.85rem;
          }
          .amenities-intro {
            margin-bottom: 1.5rem;
          }
          .amenities-grid {
            column-gap: 1.25rem;
            row-gap: 1.35rem;
          }
          .amenities-cell--gallery {
            aspect-ratio: 16 / 10;
          }
          .amenities-list-heading {
            font-size: clamp(1.2rem, 2.8vw, 1.55rem);
            margin-bottom: 0.75rem;
          }
        }

        /* Phone: stack and interleave so each gallery sits above its list. */
        @media (max-width: 639px) {
          .amenities-section {
            padding: 5rem 1rem 3.5rem;
          }
          .amenities-section::before {
            height: 60px;
          }
          .amenities-heading {
            font-size: 1.6rem;
          }
          .amenities-grid {
            grid-template-columns: 1fr;
            row-gap: 1.25rem;
          }
          .amenities-cell--cg { order: 1; }
          .amenities-cell--cl { order: 2; }
          .amenities-cell--ag { order: 3; }
          .amenities-cell--al { order: 4; }
          .amenities-cell--cl,
          .amenities-cell--al {
            margin-top: -0.25rem;
          }
          .amenities-cell--gallery {
            aspect-ratio: 4 / 3;
          }
          .amenities-list-item {
            font-size: 0.95rem;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .amenities-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slide--active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }
        .amenities-slider-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .amenities-slide-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.08);
          color: rgba(255, 255, 255, 0.7);
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: color 0.25s, transform 0.25s;
          z-index: 2;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
        }
        .amenities-slider-arrow:hover {
          color: #ffffff;
        }
        .amenities-slider-arrow--prev { left: 6px; }
        .amenities-slider-arrow--next { right: 6px; }
        .amenities-slider-arrow--prev:hover { transform: translateY(-50%) translateX(-2px); }
        .amenities-slider-arrow--next:hover { transform: translateY(-50%) translateX(2px); }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slide {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Designed for Living</p>',
    },
    'amenitieslist.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultField: 'property_amenities_blurb',
      defaultValue:
        '<p>Welcome to a world of unmatched luxury and convenience at The Aster. Our amenity offerings redefine the standard of modern living, providing an exquisite backdrop to complement your lifestyle.</p>',
    },
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.interiorHeading': {
      label: 'Interior Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Amenities</p>',
    },
    'amenitieslist.selectUnitsDisclaimer': {
      label: 'Select Units Disclaimer',
      type: 'richtext',
      defaultValue: '<p>*In Select Units</p>',
    },
  },
  slotGroups: {
    'amenitieslist.community_slide_1': communitySlide1Def,
    'amenitieslist.community_slide_2': communitySlide2Def,
    'amenitieslist.community_slide_3': communitySlide3Def,
    'amenitieslist.community_slide_4': communitySlide4Def,
    'amenitieslist.community_slide_5': communitySlide5Def,
    'amenitieslist.community_slide_6': communitySlide6Def,
    'amenitieslist.apartment_slide_1': apartmentSlide1Def,
    'amenitieslist.apartment_slide_2': apartmentSlide2Def,
    'amenitieslist.apartment_slide_3': apartmentSlide3Def,
    'amenitieslist.apartment_slide_4': apartmentSlide4Def,
    'amenitieslist.apartment_slide_5': apartmentSlide5Def,
    'amenitieslist.apartment_slide_6': apartmentSlide6Def,
  },
  collectionBindings: {
    'amenitieslist.communityPhotos': {
      label: 'Community Photos (fallback for ungenerated slides)',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos (fallback for ungenerated slides)',
      collection: 'photo_gallery',
    },
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.interiorItems': {
      label: 'Interior Amenities',
      collection: 'amenities',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesList;
